import {
    AfterViewInit,
    Component,
    EventEmitter,
    Inject,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CurrentUser } from '../../../common/providers/current-user';
import { FeedbackQuestionFactoryService } from '../../questions/feedback-question-factory.service';
import { FeedbackQuestionSurvey, QuestionProvider } from '../../providers/question-provider';
import { BreadcrumbProvider, Crumb } from '../../../breadcrumb/providers/breadcrumb-provider';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UsersProvider } from '../../../users/providers/users-provider';
import { FeedbackRights } from '../../providers/feedback-rights';
import { CurrentUserRolesProvider } from '../../../organizations/providers/current-user-roles-provider';
import { Title } from '@angular/platform-browser';
import { ConfigProvider } from "../../../config-provider.service";
import { Subscription } from 'rxjs';
import { Organization } from "../../../common/models/organization";
import { Athlete } from "../../../common/models/athlete";
import { Feedback } from "../../../common/models/feedback";
import { User } from "../../../common/models/user";
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { OrganizationRole } from "../../../common/models/organization-role";
import { PageContentComponent } from "../../../common/components/page-content/page-content";
import { HeaderComponent } from "../../../common/components/header-elitment/header-elitment";
import { SideMenuComponent } from "../../../common/components/side-menu/side-menu";
import { AthleteSideMenuComponent } from "../../../athletes/components/side-menu/side-menu";
import { Error401Component } from "../../../common/components/error-401/error-401";
import { Error404Component } from "../../../common/components/error-404/error-404";
import { ModuleContentComponent } from "../../../common/components/module-content/module-content";
import { NgIf } from "@angular/common";

@Component({
  selector: 'app-feedback-new-container',
  templateUrl: './feedback-daily-new-container.component.html',
  styleUrls: ['./feedback-daily-new-container.scss'],
  imports: [
    PageContentComponent,
    HeaderComponent,
    SideMenuComponent,
    AthleteSideMenuComponent,
    Error401Component,
    Error404Component,
    ModuleContentComponent,
    NgIf,
    TranslateModule
  ],
  standalone: true
})
export class FeedbackDailyNewContainerComponent implements OnInit, AfterViewInit, OnDestroy {
    public pageTitle = 'Tests';
    public isLoading = true;
    public is401 = false;
    public is404 = false;

    public organization: Organization = {} as Organization;
    public feedback: Feedback = {} as Feedback;
    public athleteUser: User = {} as User;
    public questions: FeedbackQuestionSurvey[] = [];

    public questionsLoaded: EventEmitter<any> = new EventEmitter();
    public rights: FeedbackRights = new FeedbackRights(this.utils);

    @ViewChild('questionComponentRef', {
        static: false,
        read: ViewContainerRef
    })
    public viewContainerRef: ViewContainerRef = {} as ViewContainerRef;

    public isSubmitting = false;

    private service;
    private subscriptions: Subscription[] = [];

    constructor(
    @Inject(FeedbackQuestionFactoryService) service:FeedbackQuestionFactoryService,
                                            private currentUser: CurrentUser,
                                            private route: ActivatedRoute,
                                            private questionProvider: QuestionProvider,
                                            private router: Router,
                                            private breadcrumbProvider: BreadcrumbProvider,
                                            private userProvider: UsersProvider,
                                            private utils: UtilsProvider,
                                            private translator: TranslateService,
                                            private currentUserRoles: CurrentUserRolesProvider,
                                            private title: Title,
                                            public conf: ConfigProvider,
                                            protected feedbackQuestionFactoryService: FeedbackQuestionFactoryService
    ) {
        this.service = service;

        this.subscriptions.push(
            feedbackQuestionFactoryService.submitSubject.subscribe((res) => {
                this.isSubmitting = res;
            })
        );
    }

    public ngOnInit(): void {
        // Load athlete and annual plan if it exists
        this.subscriptions.push(
            this.route.data.subscribe((data: any) => { //{ organization: Organization; athlete: Athlete; feedback: Feedback }
                if (data.organization) {
                    this.organization = data.organization;
                    this.service.setOrganization(data.organization);
                }

                if (data.athlete) {
                    this.athleteUser = data.athlete;
                    this.service.setAthlete(data.athlete.athlete);
                }

                if (data.feedback) {
                    this.feedback = data.feedback;
                    this.service.setFeedback(data.feedback);
                }

                Promise.all([this.loadQuestionData()]).then(() => {

                    this.rights.evaluateScreenRights({
                        rolesIds: this.currentUserRoles.getRolesIds(this.organization.id!)
                    });

                    if (!this.rights.ADD) {
                        this.is401 = true;
                    }

                    this.createPageTitle();
                    this.createBreadcrumb();
                    this.isLoading = false;
                    this.questionsLoaded.emit();
                });

                // finish survey
                this.subscriptions.push(
                    this.service.surveySaved.subscribe((event) => {
                        this.router.navigate(['/' + this.organization!.code + '/athletes/' + this.athleteUser.id + '/feedback']);
                    })
                );
            })
        );
    }

    public ngAfterViewInit(): void {

        this.service.setRootViewContainerRef(this.viewContainerRef);

        this.subscriptions.push(
            this.questionsLoaded.subscribe(() => {
                this.service.startSurvey();
            })
        );
    }

    public ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    public loadQuestionData(): Promise<any> {
        return new Promise((resolve, reject) => {
            this.subscriptions.push(
                this.questionProvider.findAll(this.organization, this.feedback).subscribe((questions: FeedbackQuestionSurvey[]) => {
                    this.questions = questions;
                    this.service.questions = questions;

                    resolve(null);
                })
            );
        });
    }

    private createPageTitle(){
        this.pageTitle = this.translator.instant('FeedbackDailyNew');
        this.title.setTitle(this.pageTitle + ' | ' + this.conf.platformName);
    }

    private createBreadcrumb() {

        const crumbs: Crumb[] = [];
        if (!this.currentUserRoles.have(OrganizationRole.ID_ATHLETE, this.organization.id!)){
            crumbs.push({
                shortTitle: 'Athletes',
                title: 'Athletes',
                url: '/' + this.organization!.code + '/athletes'
            });
        }
        crumbs.push({
            shortTitle: `${this.athleteUser.firstName?.substr(0, 1)}. ${this.athleteUser.lastName}`,
            title: `${this.athleteUser.firstName} ${this.athleteUser.lastName}`,
            url: `/${this.organization!.code}/athletes/${this.athleteUser.id}`
        });
        crumbs.push({
            shortTitle: 'Feedback',
            title: 'Feedback',
            url: `/${this.organization!.code}/athletes/${this.athleteUser.id}/feedback`
        });
        crumbs.push({
            shortTitle: 'FeedbackDailyNew',
            title: 'FeedbackDailyNew',
        });
        this.breadcrumbProvider.setDefaultCrumbs(crumbs);
    }
}
